import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { CredentialDefinitionType } from 'src/app/shared/models/credential/credential-definition.model';
import { tomorrow } from 'src/app/shared/utils/date-utils';
import { PortalBaseService } from 'src/portal/portal-base.service';
import { InvoiceModel } from '../components/profile-payments/invoice-model';

@Injectable({
  providedIn: 'root'
})
export class PaymentService extends PortalBaseService {
  public credentialName: string;
  public entityName: string;
  private _entityId: string;
  private _baseUsersPath: 'users' | 'teams' | 'portals' | 'portal-teams';

  public readonly entityInvoices = new BehaviorSubject<InvoiceModel[]>([]);

  constructor() {
    super();
  }

  async getInvoices(): Promise<InvoiceModel[]> {
    this.entityInvoices.next([]);
    const invoices = await lastValueFrom(this.get(this.invoiceUrl, !this.isPortalTeam));
    this.entityInvoices.next(invoices);
    return invoices;
  }

  sendInvoice() {
    return this.post(`${this.invoiceUrl}/send?dueDate=${tomorrow()}`, null, null);
  }

  getPaymentConfirmation = async (
    invoiceType: CredentialDefinitionType,
    invoiceId: string
  ): Promise<InvoiceModel> => {
    const getInvoiceUrl =
      invoiceType == CredentialDefinitionType.Team && this.isPerson
        ? `${this.invoiceUrl}/${invoiceId}/team`
        : `${this.invoiceUrl}/${invoiceId}`;

    return await lastValueFrom(this.get(getInvoiceUrl));
  };

  init(route: ActivatedRouteSnapshot) {
    const fullUrl = window.location.href;
    const isPortal = fullUrl.split('/').includes('portal');
    if (!route?.params) {
      console.error(`PaymentService: Route params are not available.`);
      this._entityId = null;
      this._baseUsersPath = null;
    }
    if (route.params.personId) {
      this._entityId = route.params.personId;
      this._baseUsersPath = 'users';
    } else if (route.parent?.params?.personId) {
      this._entityId = route.parent.params.personId;
      this._baseUsersPath = 'users';
    } else if (route.params.teamId) {
      this._entityId = route.params.teamId;
      this._baseUsersPath = 'teams';
    } else if (route.parent?.params?.teamId) {
      this._entityId = route.parent.params.teamId;
      this._baseUsersPath = 'teams';
    } else if (isPortal) {
      this._baseUsersPath = 'portals';
    } else {
      console.error(
        `PaymentService: Unable to initialize. Route params do not contain personId or teamId.`
      );
      this._baseUsersPath = null;
      this._entityId = null;
    }
  }

  initForPerson(personId: string) {
    this._entityId = personId;
    this._baseUsersPath = 'users';
  }

  initForTeam(teamId: string) {
    this._entityId = teamId;
    this._baseUsersPath = 'teams';
  }

  initForPortalTeam(teamId: string) {
    this._entityId = teamId;
    this._baseUsersPath = 'portal-teams';
  }

  get isPerson(): boolean {
    return this._baseUsersPath === 'users';
  }

  get isTeam(): boolean {
    return this._baseUsersPath === 'teams';
  }

  get getEntityId(): string {
    if (!this._entityId) {
      throw new Error(
        'Entity ID is not initialized. Please make sure PaymentServiceResolver is used in the route.'
      );
    }
    return this._entityId;
  }

  get invoiceUrl(): string {
    if (this._baseUsersPath === 'portals') {
      return `portal/invoices`;
    }
    if (this._baseUsersPath === 'portal-teams') {
      return `portal/teams/${this.getEntityId}/invoices`;
    }
    return `${this._baseUsersPath}/${this.getEntityId}/invoices`;
  }
  get isPortal(): boolean {
    return this._baseUsersPath === 'portals';
  }

  get isPortalTeam(): boolean {
    return this._baseUsersPath === 'portal-teams';
  }
}

@Injectable({
  providedIn: 'root'
})
export class PaymentServiceResolver {
  constructor(private paymentService: PaymentService) {}
  resolve(route: ActivatedRouteSnapshot) {
    this.paymentService.init(route);
    return this.paymentService;
  }
}
